"use client";

import { useState, useCallback } from "react";
import { motion, useMotionValue, useTransform, PanInfo } from "framer-motion";
import { Pencil, Trash2, LucideIcon } from "lucide-react";
import { triggerHaptic } from "@/hooks/use-haptics";

interface SwipeableCardProps {
  children: React.ReactNode;
  onSwipeLeft?: () => void; // Delete (default)
  onSwipeRight?: () => void; // Edit (default)
  onTap?: () => void;
  leftIcon?: LucideIcon; // Custom icon for left swipe (delete)
  rightIcon?: LucideIcon; // Custom icon for right swipe (edit)
  leftClassName?: string; // Custom background for left swipe
  rightClassName?: string; // Custom background for right swipe
}

const SWIPE_THRESHOLD = 80;

export function SwipeableCard({
  children,
  onSwipeLeft,
  onSwipeRight,
  onTap,
  leftIcon: LeftIcon = Trash2,
  rightIcon: RightIcon = Pencil,
  leftClassName = "bg-destructive",
  rightClassName = "bg-primary",
}: SwipeableCardProps) {
  const x = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);

  // Background opacity based on swipe distance
  const leftOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);
  const rightOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);

  const handleDragEnd = useCallback((_: unknown, info: PanInfo) => {
    setIsDragging(false);
    if (info.offset.x < -SWIPE_THRESHOLD && onSwipeLeft) {
      triggerHaptic("warning");
      onSwipeLeft();
    } else if (info.offset.x > SWIPE_THRESHOLD && onSwipeRight) {
      triggerHaptic("light");
      onSwipeRight();
    }
  }, [onSwipeLeft, onSwipeRight]);

  const handleTap = useCallback(() => {
    if (!isDragging && onTap) {
      triggerHaptic("selection");
      onTap();
    }
  }, [isDragging, onTap]);

  return (
    <div className="relative overflow-hidden rounded-lg" data-swipeable>
      {/* Left background (delete) - revealed when swiping left */}
      <motion.div
        className={`absolute inset-y-0 right-0 w-20 flex items-center justify-center ${leftClassName}`}
        style={{ opacity: leftOpacity }}
      >
        <LeftIcon className="h-5 w-5 text-destructive-foreground" />
      </motion.div>

      {/* Right background (edit) - revealed when swiping right */}
      <motion.div
        className={`absolute inset-y-0 left-0 w-20 flex items-center justify-center ${rightClassName}`}
        style={{ opacity: rightOpacity }}
      >
        <RightIcon className="h-5 w-5 text-primary-foreground" />
      </motion.div>

      {/* Card content */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        style={{ x, touchAction: "pan-y" }}
        onClick={handleTap}
        className="relative bg-card cursor-pointer"
      >
        {children}
      </motion.div>
    </div>
  );
}
